
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/auth";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const { error } = await resetPassword(email);
      
      if (error) {
        throw error;
      }
      
      setIsSubmitted(true);
      
      toast({
        title: "Email envoyé",
        description: "Vérifiez votre boîte de réception pour obtenir les instructions de réinitialisation du mot de passe.",
        variant: "default",
      });
    } catch (error: any) {
      console.error("Erreur de réinitialisation:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer l'email de réinitialisation. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 rounded-xl bg-white p-8 shadow-md">
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src="/lovable-uploads/60f073cd-316e-4fa1-9add-c6553bdb9ac9.png" alt="Scaloo" className="h-10 mx-auto" />
          </Link>
          
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Mot de passe oublié
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {!isSubmitted ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <Label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Adresse email
                </Label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">
                    <Mail className="h-4 w-4" />
                  </span>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="block w-full flex-1 rounded-none rounded-r-md border-gray-300 focus:border-socialsync-500 focus:ring-socialsync-500 sm:text-sm"
                    placeholder="exemple@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  className="flex w-full justify-center rounded-md border border-transparent bg-socialsync-600 px-4 py-2 text-sm font-medium text-white hover:bg-socialsync-700 focus:outline-none focus:ring-2 focus:ring-socialsync-500 focus:ring-offset-2"
                  disabled={isLoading}
                >
                  {isLoading ? "Envoi en cours..." : "Envoyer les instructions"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-6 text-center">
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      Instructions envoyées à {email}
                    </p>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-500">
                Vérifiez votre boîte de réception et suivez les instructions pour réinitialiser votre mot de passe. Si vous ne recevez pas l'email dans les prochaines minutes, vérifiez votre dossier de spam.
              </p>
            </div>
          )}

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center font-medium text-socialsync-600 hover:text-socialsync-500"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
